/*
 * <sticky-buy-button> — D-20, the "Sticky Button" section: a fixed bottom bar
 * that appears once the main product form scrolls out of view and mirrors the
 * real buy button (label, disabled, loading), the PDP price display and the
 * quantity-tier selection. Clicking it submits THE main product form, so
 * variant, quantity and gating can never disagree with the visible form.
 *
 * Live shipped this logic inline in the section. Ported to a custom element
 * per the house architecture, with three adaptations:
 *   - variant changes arrive via PUB_SUB_EVENTS.variantChange (plus the
 *     documented `variant:change` fallback event); the legacy agency events
 *     have no emitter in the rebuild;
 *   - the quantity picker is <quantity-tiers> (same .bulk-price__option DOM);
 *   - the main form is found via live's data-sticky-trigger hook first, then
 *     the rebuild's <product-info> product form.
 */
if (!customElements.get('sticky-buy-button')) {
  customElements.define(
    'sticky-buy-button',
    class StickyBuyButton extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        var root = this;
        var elImg = this.querySelector('.spb__img img');
        var elVar = this.querySelector('.spb__varname');
        var elCmp = this.querySelector('.spb__compare');
        var elPrc = this.querySelector('.spb__price');
        var elBtn = this.querySelector('.spb__btn');
        var cfgEl = this.querySelector('[data-sticky-config]');
        if (!elBtn || !cfgEl) return;

        var cfg = JSON.parse(cfgEl.textContent);
        var BTN_TEXT = cfg.btnLabel;
        var SOLD_OUT = cfg.soldOut;

        function getMainForm() {
          return (
            document.querySelector('product-form[data-sticky-trigger]') ||
            document.querySelector('product-info product-form') ||
            document.querySelector('.product-form')
          );
        }

        function getRealBtn() {
          var form = getMainForm();
          return form ? form.querySelector('button[name="add"]') : null;
        }

        var moneyFormat = document.body.getAttribute('data-money-format') || cfg.moneyFormat || '{{amount}}';

        function formatMoney(cents) {
          cents = parseInt(cents, 10);
          if (isNaN(cents)) return '';
          var amount = (cents / 100).toFixed(2);
          var parts = amount.split('.');
          var whole = parts[0];
          var dec = parts[1];
          var fmt = moneyFormat;
          var formatted;
          if (fmt.indexOf('amount_no_decimals') !== -1) {
            formatted = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g,
              fmt.indexOf('with_space_separator') !== -1 ? '$1 ' :
              fmt.indexOf('with_comma_separator') !== -1 ? '$1,' : '$1,');
          } else if (fmt.indexOf('amount_with_comma_separator') !== -1) {
            formatted = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1.') + ',' + dec;
          } else if (fmt.indexOf('amount_with_space_separator') !== -1) {
            formatted = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1 ') + '.' + dec;
          } else {
            formatted = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,') + '.' + dec;
          }
          return fmt.replace(/\{\{\s*\w+\s*\}\}/, formatted);
        }

        function updateContent() {
          var currentOption = document.querySelector('.bulk-price__option--current');

          var priceText = '';
          if (currentOption) {
            var priceRaw = currentOption.getAttribute('data-price');
            if (priceRaw) priceText = formatMoney(priceRaw);
          }
          if (!priceText) {
            var priceEl = document.querySelector('.price-item--sale.igPrice')
                       || document.querySelector('.price-item--regular.igPrice');
            if (priceEl) priceText = priceEl.textContent.trim();
          }
          if (priceText) elPrc.textContent = priceText;

          var compareText = '';
          if (currentOption) {
            var cmpRaw = currentOption.getAttribute('data-compare-at-price');
            var priceRaw2 = currentOption.getAttribute('data-price');
            if (cmpRaw && parseInt(cmpRaw, 10) > parseInt(priceRaw2, 10)) {
              compareText = formatMoney(cmpRaw);
            }
          }
          if (!compareText) {
            var cmpEl = document.querySelector('.price-item--regular.igComparePrice');
            if (cmpEl && cmpEl.textContent.trim()) {
              var priceCont = cmpEl.closest('.price');
              if (priceCont && priceCont.classList.contains('price--on-sale')) {
                compareText = cmpEl.textContent.trim();
              }
            }
          }
          if (compareText) {
            elCmp.textContent = compareText;
            elCmp.style.display = '';
          } else {
            elCmp.style.display = 'none';
          }

          if (currentOption && elVar) {
            var optLabelEl = currentOption.querySelector('.bulk-price__option-label');
            var optLabel = '';
            if (optLabelEl) {
              optLabel = optLabelEl.textContent.trim();
            } else {
              currentOption.childNodes.forEach(function (n) {
                if (n.nodeType === 3 && n.textContent.trim()) optLabel = n.textContent.trim();
              });
            }
            if (optLabel) {
              elVar.textContent = optLabel;
              elVar.style.display = '';
            }
          }

          var realBtn = getRealBtn();
          if (realBtn && !elBtn.classList.contains('loading') && !elBtn.classList.contains('success')) {
            elBtn.disabled = realBtn.disabled;
            var span = elBtn.querySelector('span');
            if (span) span.textContent = realBtn.disabled ? SOLD_OUT : BTN_TEXT;
          }
        }

        function syncImage(v) {
          var src = null;
          if (v && v.featured_media && v.featured_media.preview_image) {
            src = v.featured_media.preview_image.src.replace(
              /(_\d+x\d*)?(\.[^.?]+)(\?.*)?$/,
              function (_, size, ext, qs) { return '_120x' + ext + (qs || ''); }
            );
          } else if (cfg.defaultImg) {
            src = cfg.defaultImg;
          }
          if (src && elImg) { elImg.src = src; elImg.style.display = ''; }
        }

        var formOutOfView = false;
        var mobileMQ = window.matchMedia('(max-width: 749px)');

        function isOverlayOpen() {
          var bodyLocked = false;
          document.body.classList.forEach(function (c) {
            if (c.indexOf('overflow-hidden') === 0) bodyLocked = true;
          });
          return bodyLocked
            || !!document.querySelector('cart-drawer.active, dialog[open], details-modal[open], modal-dialog[open]');
        }

        function applyVisibility() {
          var show = formOutOfView && !(mobileMQ.matches && isOverlayOpen());
          root.classList.toggle('is-visible', show);
          root.setAttribute('aria-hidden', show ? 'false' : 'true');
        }

        var targetForm = getMainForm();
        if (targetForm) {
          this.formObserver = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              formOutOfView = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              applyVisibility();
            });
          }, { threshold: 0 });
          this.formObserver.observe(targetForm);
        }

        this.bodyObserver = new MutationObserver(applyVisibility);
        this.bodyObserver.observe(document.body, {
          attributes: true, attributeFilter: ['class']
        });
        if (mobileMQ.addEventListener) {
          mobileMQ.addEventListener('change', applyVisibility);
        }

        var mirrorActive = false;
        var self = this;
        function mirrorRealBtn() {
          if (mirrorActive) return;
          var realBtn = getRealBtn();
          if (!realBtn) return;
          mirrorActive = true;

          var mySpan = elBtn.querySelector('span');
          var mySpinner = elBtn.querySelector('.loading__spinner');

          self.mirrorObserver = new MutationObserver(function (mutations) {
            mutations.forEach(function (m) {
              if (m.type === 'attributes') {
                var isLoading = realBtn.classList.contains('loading');
                var isSuccess = realBtn.classList.contains('success');

                elBtn.classList.toggle('loading', isLoading);
                elBtn.classList.toggle('success', isSuccess);

                if (mySpinner) mySpinner.classList.toggle('hidden', !isLoading);

                var ariaDisabled = realBtn.getAttribute('aria-disabled');
                if (ariaDisabled === 'true') {
                  elBtn.setAttribute('aria-disabled', 'true');
                } else {
                  elBtn.removeAttribute('aria-disabled');
                }
                elBtn.disabled = realBtn.disabled;
              }

              if (m.type === 'characterData' || m.type === 'childList') {
                var realSpan = realBtn.querySelector('[data-buy-button-label]') || realBtn.querySelector('span');
                if (realSpan && mySpan) mySpan.textContent = realSpan.textContent.trim();
              }
            });
          });
          self.mirrorObserver.observe(realBtn, {
            attributes: true,
            attributeFilter: ['class', 'disabled', 'aria-disabled'],
            subtree: true,
            characterData: true,
            childList: true
          });
        }

        elBtn.addEventListener('click', function (e) {
          e.preventDefault();
          var productFormEl = getMainForm();
          if (!productFormEl) return;

          var typeBtn = productFormEl.querySelector('button[type="button"][name="add"]:not([disabled])');
          if (typeBtn) { typeBtn.click(); return; }

          var submitBtn = productFormEl.querySelector('[type="submit"][name="add"]:not([disabled])');
          var form = productFormEl.querySelector('form');
          if (!submitBtn || !form) return;

          try {
            form.requestSubmit(submitBtn);
          } catch (_) {
            submitBtn.click();
          }
        });

        var syncTimer;
        function scheduleUpdate(delay) {
          clearTimeout(syncTimer);
          syncTimer = setTimeout(updateContent, delay || 100);
        }

        this.syncObserver = new MutationObserver(function () { scheduleUpdate(100); });
        var priceContainer = document.querySelector('.price__container');
        if (priceContainer) {
          this.syncObserver.observe(priceContainer, { childList: true, subtree: true, characterData: true });
        }
        var qtyContainer = document.querySelector('quantity-tiers') || targetForm;
        if (qtyContainer) {
          this.syncObserver.observe(qtyContainer, {
            childList: true, subtree: true, attributes: true,
            attributeFilter: ['class', 'checked']
          });
          qtyContainer.addEventListener('click', function () { scheduleUpdate(150); });
          qtyContainer.addEventListener('change', function () { scheduleUpdate(150); });
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
            var v = event && event.data && event.data.variant;
            if (!v) return;
            syncImage(v);
            if (elVar) {
              if (v.title && v.title !== 'Default Title') {
                elVar.textContent = v.title;
                elVar.style.display = '';
              } else {
                elVar.style.display = 'none';
              }
            }
            scheduleUpdate(50);
          });
        }
        document.addEventListener('variant:change', function () { scheduleUpdate(50); });
        document.addEventListener('change', function (e) {
          if (e.target.closest('quantity-tiers') || e.target.closest('.product-form')) {
            scheduleUpdate(200);
          }
        });

        updateContent();
        mirrorRealBtn();
        setTimeout(function () { updateContent(); mirrorRealBtn(); }, 400);
      }

      disconnectedCallback() {
        if (this.formObserver) this.formObserver.disconnect();
        if (this.bodyObserver) this.bodyObserver.disconnect();
        if (this.mirrorObserver) this.mirrorObserver.disconnect();
        if (this.syncObserver) this.syncObserver.disconnect();
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }
    }
  );
}
